import { useState, useEffect } from "react";
import { api, useAuth } from "../App";
import { Calendar } from "../components/ui/calendar";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Car, Loader2, Lock, Unlock } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function HostCalendarPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [bookedDates, setBookedDates] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDates, setSelectedDates] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchVehicles();
  }, [user]);

  useEffect(() => {
    if (selectedVehicle) {
      fetchAvailability();
    }
  }, [selectedVehicle]);

  const fetchVehicles = async () => {
    if (!user?.user_id) return;
    try {
      const response = await api.get(`/vehicles/host/${user.user_id}`);
      const vehiclesList = response.data || [];
      setVehicles(vehiclesList);
      if (vehiclesList.length > 0) {
        setSelectedVehicle(vehiclesList[0].vehicle_id);
      }
    } catch (error) {
      console.error("Error fetching vehicles:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailability = async () => {
    try {
      const response = await api.get(`/vehicles/${selectedVehicle}/availability`);

      const rawBlocks = response.data.blocks || [];
      setBlocks(rawBlocks);

      // Parse blocked dates
      const blocked = rawBlocks.flatMap((block) => {
        const dates = [];
        const start = new Date(block.start_date);
        const end = new Date(block.end_date);
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          dates.push(new Date(d));
        }
        return dates;
      });
      setBlockedDates(blocked);

      // Parse booked dates
      const booked = (response.data.booked_dates || []).flatMap((booking) => {
        const dates = [];
        const start = new Date(booking.pickup_date);
        const end = new Date(booking.return_date);
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          dates.push(new Date(d));
        }
        return dates;
      });
      setBookedDates(booked);
    } catch (error) {
      console.error("Error fetching availability:", error);
    }
  };

  const handleBlockDates = async () => {
    if (selectedDates.length === 0) return;
    const sortedDates = [...selectedDates].sort((a, b) => a - b);
    setActionLoading(true);
    try {
      await api.post(`/vehicles/${selectedVehicle}/availability`, {
        vehicle_id: selectedVehicle,
        start_date: sortedDates[0].toISOString(),
        end_date: sortedDates[sortedDates.length - 1].toISOString(),
        reason: "blocked",
      });
      setSelectedDates([]);
      await fetchAvailability();
    } catch (error) {
      console.error("Error blocking dates:", error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnblockSelected = async () => {
    if (selectedDates.length === 0) return;
    setActionLoading(true);
    try {
      await api.post(`/vehicles/${selectedVehicle}/availability/unblock`, {
        dates: selectedDates.map((d) => d.toISOString()),
      });
      setSelectedDates([]);
      await fetchAvailability();
    } catch (error) {
      console.error("Error unblocking dates:", error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteBlock = async (blockId) => {
    setActionLoading(true);
    try {
      await api.delete(`/vehicles/${selectedVehicle}/availability/${blockId}`);
      await fetchAvailability();
    } catch (error) {
      console.error("Error deleting block:", error);
    } finally {
      setActionLoading(false);
    }
  };

  const isDateBooked = (date) => {
    return bookedDates.some(
      (d) => d.toDateString() === date.toDateString()
    );
  };

  const isDateBlocked = (date) => {
    return blockedDates.some(
      (d) => d.toDateString() === date.toDateString()
    );
  };

  // Count selected dates that are currently blocked vs free
  const selectedBlockedCount = selectedDates.filter((d) => isDateBlocked(d)).length;
  const selectedFreeCount = selectedDates.length - selectedBlockedCount;

  const formatDateRange = (start, end) => {
    const s = new Date(start);
    const e = new Date(end);
    const opts = { month: "short", day: "numeric", year: "numeric" };
    if (s.toDateString() === e.toDateString()) return s.toLocaleDateString(undefined, opts);
    return `${s.toLocaleDateString(undefined, opts)} → ${e.toLocaleDateString(undefined, opts)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-terracotta" />
      </div>
    );
  }

  if (vehicles.length === 0) {
    return (
      <div className="min-h-screen bg-sand">
        <div className="container-main py-8">
          <div className="text-center py-20">
            <Car className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              {t("hostCal.noCarsTitle")}
            </h3>
            <p className="text-gray-500">
              {t("hostCal.noCarsBody")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const currentVehicle = vehicles.find((v) => v.vehicle_id === selectedVehicle);

  return (
    <div className="min-h-screen bg-sand" data-testid="host-calendar-page">
      <div className="container-main py-8">
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-agave mb-6">
          {t("hostCal.title")}
        </h1>

        {/* Vehicle Selector */}
        <div className="card-base mb-6">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <label className="text-sm font-medium text-gray-600 mb-2 block">
                {t("hostCal.selectVehicle")}
              </label>
              <Select value={selectedVehicle} onValueChange={setSelectedVehicle}>
                <SelectTrigger data-testid="vehicle-selector">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {vehicles.map((vehicle) => (
                    <SelectItem key={vehicle.vehicle_id} value={vehicle.vehicle_id}>
                      {vehicle.make} {vehicle.model} {vehicle.year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {currentVehicle && (
              <img
                src={currentVehicle.photos?.[0] || "https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=100"}
                alt=""
                className="w-20 h-20 rounded-xl object-cover"
              />
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar */}
          <div className="lg:col-span-2 card-base">
            <Calendar
              mode="multiple"
              selected={selectedDates}
              onSelect={setSelectedDates}
              className="w-full"
              modifiers={{
                booked: bookedDates,
                blocked: blockedDates,
              }}
              modifiersClassNames={{
                booked: "bg-blue-100 text-blue-800",
                blocked: "bg-red-100 text-red-800 line-through",
              }}
              disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0)) || isDateBooked(date)}
            />
            <p className="text-xs text-gray-500 mt-3">
              {t("hostCal.tip")}
            </p>
          </div>

          {/* Legend & Actions */}
          <div className="space-y-6">
            {/* Legend */}
            <div className="card-base">
              <h3 className="font-heading font-semibold mb-4">{t("hostCal.legend")}</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded bg-blue-100 border border-blue-200" />
                  <span className="text-sm">{t("hostCal.booked")}</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded bg-red-100 border border-red-200" />
                  <span className="text-sm">{t("hostCal.blocked")}</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded bg-terracotta border border-terracotta" />
                  <span className="text-sm">{t("hostCal.selected")}</span>
                </div>
              </div>
            </div>

            {/* Block / Unblock Dates */}
            <div className="card-base">
              <h3 className="font-heading font-semibold mb-4">{t("hostCal.manageDates")}</h3>
              <p className="text-sm text-gray-600 mb-4">
                {t("hostCal.manageDatesBody")}
              </p>
              {selectedDates.length > 0 && (
                <div className="mb-4 text-sm space-y-1">
                  <p className="font-medium">
                    {t("hostCal.selectedCount", { count: selectedDates.length })}
                  </p>
                  {selectedFreeCount > 0 && (
                    <p className="text-gray-600">{t("hostCal.freeCount", { count: selectedFreeCount })}</p>
                  )}
                  {selectedBlockedCount > 0 && (
                    <p className="text-gray-600">{t("hostCal.blockedCount", { count: selectedBlockedCount })}</p>
                  )}
                </div>
              )}
              <div className="space-y-2">
                <Button
                  onClick={handleBlockDates}
                  disabled={selectedFreeCount === 0 || actionLoading}
                  className="w-full btn-primary"
                  data-testid="block-dates-btn"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  {t("hostCal.blockSelected")}
                </Button>
                <Button
                  onClick={handleUnblockSelected}
                  disabled={selectedBlockedCount === 0 || actionLoading}
                  variant="outline"
                  className="w-full"
                  data-testid="unblock-dates-btn"
                >
                  <Unlock className="w-4 h-4 mr-2" />
                  {t("hostCal.unblockSelected")}
                </Button>
              </div>
            </div>

            {/* Existing Blocks */}
            {blocks.length > 0 && (
              <div className="card-base" data-testid="blocks-list">
                <h3 className="font-heading font-semibold mb-4">{t("hostCal.activeBlocks")}</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {blocks.map((block) => (
                    <div
                      key={block.block_id}
                      className="flex items-center justify-between gap-3 p-3 rounded-lg bg-red-50 border border-red-100"
                      data-testid={`block-row-${block.block_id}`}
                    >
                      <div className="text-sm">
                        <p className="font-medium text-gray-800">
                          {formatDateRange(block.start_date, block.end_date)}
                        </p>
                        {block.reason && block.reason !== "blocked" && (
                          <p className="text-xs text-gray-500">{block.reason}</p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteBlock(block.block_id)}
                        disabled={actionLoading}
                        className="text-red-600 hover:bg-red-100"
                        data-testid={`delete-block-${block.block_id}`}
                      >
                        <Unlock className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
